<template>
  <div class="download-menu">
    <!-- :href="`#${content.id}`" -->
    <router-link
      class="download-menu__anchor"
      :to="{ query: route.query, params: route.params, hash: `#${content.id}` }"
      @click="$emit('changeMenu')"
    >
      <h2 class="download-menu__anchor__label">{{ content.title }}</h2>
      <icon
        :data="pageIcons[content.icon]"
        class="download-menu__anchor__icon"
        v-if="tvaMq === 'desktop'"
      ></icon>
    </router-link>
    <p class="download-menu__subtitle">{{ content.subtitle }}</p>
    <a
      class="download-menu__book"
      target="_blank"
      @click="
        digitalData({
          event: content.book.event,
          eventDetail: content.book.eventDetail,
          contentTitle: content.book.title,
          contentUrl: content.pdfLink,
          ctaLabel: content.book.title,
        })
      "
      :href="
        $route.query.book === 'noidieni'
          ? content.book.pdfLink
          : `${$baseUrl}documents/${content.book.pdfLink}.pdf`
      "
    >
      <div>
        <p class="download-menu__book__title">{{ content.book.title }}</p>
        <p class="download-menu__book__subtitle">{{ content.book.subtitle }}</p>
      </div>
      <icon
        :data="pageIcons[content.book.icon]"
        class="download-menu__book__icon"
      ></icon>
    </a>
    <div class="download-menu__interviews" v-if="content.interviewsSection">
      <p class="download-menu__interviews__title">
        {{ content.interviewsSection.title }}
      </p>
      <ul class="download-menu__interviews__list">
        <a
          class="download-menu__interviews__list__item"
          v-for="interview in content.interviewsSection.interviews"
          :key="interview.name"
          target="_blank"
          :href="`${$baseUrl}documents/${interview.pdfLink}.pdf`"
          @click="
            digitalData({
              event: interview.event,
              eventDetail: interview.eventDetail,
              contentTitle: interview.name,
              contentUrl: interview.pdfLink,
              ctaLabel: interview.name,
            })
          "
        >
          <p>{{ interview.name }}</p>
          <icon
            :data="pageIcons[content.interviewsSection.icon]"
            class="download-menu__interviews__list__item__icon"
          ></icon>
        </a>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { inject } from "vue";
import { useRoute } from "vue-router";
import { pageIcons } from "../../plugins/page-icons";
import { useGoogleAnalytics } from "../../plugins/useGoogleAnalitycs";

const { digitalData } = useGoogleAnalytics();
const route = useRoute();

defineProps({
  content: Object,
});
const tvaMq = inject("$tvaMq");
</script>

<style lang="scss" scoped>
.download-menu {
  text-align: left;
  margin-top: toRem(53);

  &__anchor {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;

    &__label {
      margin-bottom: toRem(13);
      @include p-l-bold;
    }

    &__icon {
      min-height: 1.5rem;
      min-width: 1.5rem;
      color: $primary-yellow;
      opacity: 0;
      transition: opacity 0.3s linear;
    }

    &:hover {
      .download-menu__anchor__icon {
        opacity: 1;
      }
    }
  }

  &__subtitle {
    margin-bottom: toRem(16);
    @include p-m;
  }

  &__book {
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    margin-bottom: toRem(36);
    position: relative;

    &__title {
      text-transform: uppercase;
      margin-bottom: toRem(5);
      @include link-s;
    }

    &__subtitle {
      @include dida-m;
      font-style: italic;
    }

    &__icon {
      min-height: toRem(17);
      min-width: toRem(15);
      :deep(path) {
        transition: all 0.3s ease;
        fill: $scala-grigi-gray_1;

        &:last-child {
          stroke: $scala-grigi-gray_1;
        }
      }
    }

    &:before {
      content: "";
      position: absolute;
      bottom: -16px;
      height: 1px;
      width: 100%;
      background-color: $scala-grigi-gray_1;
      opacity: 0.3;
    }

    &:hover {
      .download-menu__book__icon {
        :deep(path) {
          fill: $primary-yellow;
          &:last-child {
            stroke: $primary-yellow;
          }
        }
      }
    }
  }

  &__interviews {
    &__title {
      margin-bottom: toRem(19);
      @include p-m;
    }

    &__list {
      &__item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        text-transform: uppercase;
        cursor: pointer;
        @include link-s;

        &__icon {
          min-height: toRem(17);
          min-width: toRem(15);
          :deep(path) {
            transition: all 0.3s ease;
            fill: $scala-grigi-gray_1;
            &:last-child {
              stroke: $scala-grigi-gray_1;
            }
          }
        }

        &:hover {
          .download-menu__interviews__list__item__icon {
            :deep(path) {
              fill: $primary-yellow;
              &:last-child {
                stroke: $primary-yellow;
              }
            }
          }
        }

        &:not(:first-child) {
          margin-top: toRem(11);
        }
      }
    }
  }
}

.tablet,
.mobile {
  .download-menu {
    margin-top: toRem(72);
    
    &__anchor {
      &__icon {
        opacity: 1;
      }
    }
    &__book {
      margin-bottom: toRem(41);

      &__icon {
        :deep(path) {
          fill: $primary-yellow;

          &:last-child {
            stroke: $primary-yellow;
          }
        }
      }

      &:before {
        bottom: -25px;
      }
    }
    &__interviews {
      &__list {
        &__item {
          &__icon {
            :deep(path) {
              fill: $primary-yellow;
              &:last-child {
                stroke: $primary-yellow;
              }
            }
          }
        }
      }
    }
  }
}

.mobile {
  .download-menu {
    margin-top: toRem(52);

    &__anchor {
      &__label {
        @include title-h-5;
      }
    }

    &__subtitle {
      margin-bottom: toRem(10);
    }

    &__book {
      margin-bottom: toRem(29);

      &__title {
        @include link-s;
      }

      &::before {
        bottom: -19px;
      }
    }

    &__interviews {
      &__title {
        margin-bottom: toRem(13);
      }
    }
  }
}
</style>
